import {useEffect, useRef} from 'react';
import {useAuth, useAutoSignin} from 'react-oidc-context';
import {fetchCurrentUser} from '@/services/global-services/currentUserService.js';
import store from '@/store/store.js';
import {setCurrentUser, setRoles, setCurrentUserError, setCurrentUserLoading} from '@/store/slices/authSlice.js';
import {jwtDecode} from 'jwt-decode';
import {useLogout} from '@/hooks/global-hooks/useLogout.js';
import logoWithBrand from '/src/assets/images/logoWithBrand.png';
import {useAppSelector} from '@/store/hooks.js';

export const ProtectedApp = ({children}) => {

    const {logout} = useLogout();

    const auth = useAuth();
    const {
        isLoading: isKeycloakLoading,
        isAuthenticated,
        error: keycloakError
    } = useAutoSignin({signinMethod: 'signinRedirect'});

    const currentUserError = useAppSelector((state) => state.auth.error);
    const currentUser = useAppSelector((state) => state.auth.currentUser);
    const isCurrentUserLoading = useAppSelector((state) => state.auth.loading);

    useEffect(() => {

        const loadUser = async () => {

            if (keycloakError) {
                console.log('🔒🚨 Keycloak loading error: ', keycloakError);
                // attendre 2s et procéder à un logout
                setTimeout(() => {
                    logout();
                }, 2000);
            }

            if (!isKeycloakLoading && isAuthenticated && !currentUser) {

                console.log('🔒 Keycloak loaded successfully, examining token...');
                const tokenData = jwtDecode(auth.user.access_token);

                // Vérification de l'expiration du token
                if (Date.now() >= tokenData.exp * 1000) {
                    console.log('🔒 Expired token. Force logout...');
                    await logout();
                    return;
                }

                // Si currentUser a déjà été chargé, pas besoin de le recharger à nouveau
                if (!currentUser) {

                    // Appel à currentUser après authentification réussie
                    console.log('🔒 Authentication successful, loading user data...');
                    const currentUserData = await fetchCurrentUser();

                    if (currentUserData.error) {
                        console.error('🔒🚨 Error loading user data: ', currentUserData.error);
                        store.dispatch(setCurrentUserError(currentUserData.error.toString()));
                    } else {

                        // Stocker currentUser et les rôles dans Redux
                        store.dispatch(setCurrentUser(currentUserData.data));
                        const roles = tokenData?.realm_access?.roles || [];
                        store.dispatch(setRoles(roles));
                    }

                    store.dispatch(setCurrentUserLoading(false));
                }
            }
        };
        loadUser();

    }, [isKeycloakLoading, keycloakError, isCurrentUserLoading, isAuthenticated]);

    if (isKeycloakLoading || !isAuthenticated || keycloakError || isCurrentUserLoading || currentUserError) {
        return <div className="loading-container">
            <img src={logoWithBrand} alt="Synapse Logo" style={{height: '350px', width: 'auto', objectFit: 'contain'}}/>
            <div className="loading-content">
                <p className="text">Welcome to</p>
                <h1 className="loading-title text">Kubrik 21</h1>
                <p className="text">
                    {isKeycloakLoading && 'Keycloak initialization...'}
                    {!isKeycloakLoading && isAuthenticated && isCurrentUserLoading && 'Loading user data...'}
                    {keycloakError && 'Error loading Keycloak'}
                    {currentUserError && 'You don\'t have access to Kubrik 21'}
                    {!isKeycloakLoading && !isAuthenticated && !keycloakError && 'You must be logged in to access this page'}
                </p>
            </div>
        </div>;
    }

    return <>{children}</>;
};
